import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Download, Store, Users, Plus, Pencil, Copy, CheckCircle, XCircle, Eye, Trash2, CheckCircle2, Circle, Settings, Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { EditLetterTemplateDialog } from "@/components/EditLetterTemplateDialog";
import { EditSponsorshipDialog } from "@/components/EditSponsorshipDialog";
import { EditDonorDialog } from "@/components/EditDonorDialog";
import { LetterBuilder } from "@/components/LetterBuilder";
export default function CommunityDashboard() {
  const {
    user
  } = useAuth();
  const [templates, setTemplates] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [sponsorships, setSponsorships] = useState<any[]>([]);
  const [donors, setDonors] = useState<any[]>([]);
  const [filteredDonors, setFilteredDonors] = useState<any[]>([]);
  const [donorSearchTerm, setDonorSearchTerm] = useState("");
  const [donorStatusFilter, setDonorStatusFilter] = useState<string>("all");
  const [donorResponseFilter, setDonorResponseFilter] = useState<string>("all");
  const [donorDateFrom, setDonorDateFrom] = useState("");
  const [donorDateTo, setDonorDateTo] = useState("");
  const [settings, setSettings] = useState<any>(null);
  const [ptaId, setPtaId] = useState<string>("");
  const [applicationLink, setApplicationLink] = useState("");
  const [editTemplateDialog, setEditTemplateDialog] = useState(false);
  const [editSponsorshipDialog, setEditSponsorshipDialog] = useState(false);
  const [editDonorDialog, setEditDonorDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [selectedSponsorship, setSelectedSponsorship] = useState<any>(null);
  const [selectedDonor, setSelectedDonor] = useState<any>(null);
  
  // Tasks state
  const [tasks, setTasks] = useState<any[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [taskType, setTaskType] = useState<'personal' | 'general'>('personal');
  useEffect(() => {
    if (user?.id) {
      fetchPTAId();
    }
  }, [user]);
  useEffect(() => {
    if (ptaId) {
      fetchTemplates();
      fetchApplications();
      fetchSponsorships();
      fetchDonors();
      fetchSettings();
      fetchTasks();
      setApplicationLink(`/stall-application/${ptaId}`);
    }
  }, [ptaId]);

  useEffect(() => {
    // Listen for template saved events to refresh the list
    const handleTemplateSaved = () => {
      fetchTemplates();
    };
    
    window.addEventListener('template-saved', handleTemplateSaved);
    return () => window.removeEventListener('template-saved', handleTemplateSaved);
  }, [ptaId]);
  const fetchPTAId = async () => {
    const {
      data
    } = await supabase.from("members").select("pta_id").eq("user_id", user?.id).single();
    if (data) {
      setPtaId(data.pta_id);
    }
  };
  const fetchTemplates = async () => {
    const {
      data
    } = await supabase.from("community_letter_templates").select("*").eq("pta_id", ptaId).order("created_at", {
      ascending: false
    });
    if (data) {
      setTemplates(data);
    }
  };
  const fetchApplications = async () => {
    const {
      data
    } = await supabase.from("stall_applications").select(`
        *,
        events (name, event_date)
      `).eq("pta_id", ptaId).order("created_at", {
      ascending: false
    });
    if (data) {
      setApplications(data);
    }
  };
  const fetchSponsorships = async () => {
    const {
      data
    } = await supabase.from("business_sponsorships").select("*").eq("pta_id", ptaId).order("sponsored_date", {
      ascending: false
    });
    if (data) {
      setSponsorships(data);
    }
  };
  
  const fetchDonors = async () => {
    const { data } = await supabase
      .from("donors")
      .select("*")
      .eq("pta_id", ptaId)
      .order("contact_date", { ascending: false });
    if (data) {
      setDonors(data);
      setFilteredDonors(data);
    }
  };

  // Filter donors when search/filter criteria change
  useEffect(() => {
    let filtered = [...donors];

    // Search by name
    if (donorSearchTerm.trim()) {
      filtered = filtered.filter(donor =>
        donor.name.toLowerCase().includes(donorSearchTerm.toLowerCase())
      );
    }

    // Filter by donation status
    if (donorStatusFilter !== "all") {
      filtered = filtered.filter(donor =>
        donorStatusFilter === "donated" ? donor.donated : !donor.donated
      );
    }

    // Filter by response status
    if (donorResponseFilter !== "all") {
      filtered = filtered.filter(donor =>
        donorResponseFilter === "responded" ? donor.responded : !donor.responded
      );
    }

    // Filter by date range
    if (donorDateFrom) {
      filtered = filtered.filter(donor =>
        new Date(donor.contact_date) >= new Date(donorDateFrom)
      );
    }
    if (donorDateTo) {
      filtered = filtered.filter(donor =>
        new Date(donor.contact_date) <= new Date(donorDateTo)
      );
    }

    setFilteredDonors(filtered);
  }, [donors, donorSearchTerm, donorStatusFilter, donorResponseFilter, donorDateFrom, donorDateTo]);
  
  const fetchSettings = async () => {
    let {
      data
    } = await supabase.from("stall_application_settings").select("*").eq("pta_id", ptaId).single();
    if (!data) {
      const {
        data: newSettings
      } = await supabase.from("stall_application_settings").insert({
        pta_id: ptaId
      }).select().single();
      data = newSettings;
    }
    if (data) {
      setSettings(data);
    }
  };
  const handleUpdateSettings = async (field: string, value: any) => {
    const {
      error
    } = await supabase.from("stall_application_settings").update({
      [field]: value
    }).eq("pta_id", ptaId);
    if (error) {
      toast.error("Failed to update settings");
    } else {
      setSettings({
        ...settings,
        [field]: value
      });
      toast.success("Settings updated");
    }
  };
  const handleApproveApplication = async (id: string) => {
    // Get full application details for email
    const { data: application, error: fetchError } = await supabase
      .from("stall_applications")
      .select(`
        *,
        events (name, event_date),
        ptas (name)
      `)
      .eq("id", id)
      .single();

    if (fetchError || !application) {
      toast.error("Failed to fetch application details");
      return;
    }

    const { error } = await supabase
      .from("stall_applications")
      .update({ status: "approved" })
      .eq("id", id);

    if (error) {
      toast.error("Failed to approve application");
    } else {
      // Send approval email
      try {
        await supabase.functions.invoke("send-email", {
          body: {
            templateName: "stall_application_approved",
            recipients: [application.contact_email],
            dynamicData: {
              business_name: application.business_name,
              event_name: application.events?.name || "the event",
              event_date: new Date(application.events?.event_date).toLocaleDateString(),
              pta_name: application.ptas?.name || "the PTA",
            },
          },
        });
      } catch (emailError) {
        console.error("Failed to send approval email:", emailError);
        // Don't show error to user as the main action succeeded
      }
      
      toast.success("Application approved and email sent");
      fetchApplications();
    }
  };
  const handleDeclineApplication = async (id: string) => {
    // Get full application details for email
    const { data: application, error: fetchError } = await supabase
      .from("stall_applications")
      .select(`
        *,
        events (name, event_date),
        ptas (name)
      `)
      .eq("id", id)
      .single();

    if (fetchError || !application) {
      toast.error("Failed to fetch application details");
      return;
    }

    const { error } = await supabase
      .from("stall_applications")
      .update({ status: "declined" })
      .eq("id", id);

    if (error) {
      toast.error("Failed to decline application");
    } else {
      // Send decline email
      try {
        await supabase.functions.invoke("send-email", {
          body: {
            templateName: "stall_application_declined",
            recipients: [application.contact_email],
            dynamicData: {
              business_name: application.business_name,
              event_name: application.events?.name || "the event",
              event_date: new Date(application.events?.event_date).toLocaleDateString(),
              pta_name: application.ptas?.name || "the PTA",
            },
          },
        });
      } catch (emailError) {
        console.error("Failed to send decline email:", emailError);
        // Don't show error to user as the main action succeeded
      }
      
      toast.success("Application declined and email sent");
      fetchApplications();
    }
  };
  const handleDeleteApplication = async (id: string) => {
    const {
      error
    } = await supabase.from("stall_applications").delete().eq("id", id);
    if (error) {
      toast.error("Failed to delete application");
    } else {
      toast.success("Application deleted");
      fetchApplications();
    }
  };
  const handleDeleteSponsorship = async (id: string) => {
    const {
      error
    } = await supabase.from("business_sponsorships").delete().eq("id", id);
    if (error) {
      toast.error("Failed to delete sponsorship");
    } else {
      toast.success("Sponsorship deleted");
      fetchSponsorships();
    }
  };
  
  const handleDeleteDonor = async (id: string) => {
    const { error } = await supabase
      .from("donors")
      .delete()
      .eq("id", id);
    if (error) {
      toast.error("Failed to delete donor");
    } else {
      toast.success("Donor deleted");
      fetchDonors();
    }
  };
  
  const handleDeleteTemplate = async (id: string) => {
    const {
      error
    } = await supabase.from("community_letter_templates").delete().eq("id", id);
    if (error) {
      toast.error("Failed to delete template");
    } else {
      toast.success("Template deleted");
      fetchTemplates();
    }
  };
  const copyToClipboard = () => {
    navigator.clipboard.writeText(applicationLink);
    toast.success("Link copied to clipboard");
  };
  
  const fetchTasks = async () => {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .or(`assigned_to.eq.${user?.id},created_by.eq.${user?.id},task_type.eq.general`)
      .order('created_at', { ascending: false });

    if (!error && data) setTasks(data as any);
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    const { error } = await supabase.from('tasks').insert({
      title: newTaskTitle,
      description: newTaskDescription || null,
      task_type: taskType,
      assigned_to: taskType === 'personal' ? user.id : null,
      created_by: user.id,
      status: 'pending',
    });

    if (error) {
      toast.error("Failed to add task");
      return;
    }

    toast.success("Task added successfully");
    setNewTaskTitle("");
    setNewTaskDescription("");
    fetchTasks();
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    const { error } = await supabase
      .from('tasks')
      .update({ 
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null 
      })
      .eq('id', taskId);

    if (!error) {
      fetchTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (!error) {
      toast.success("Task deleted");
      fetchTasks();
    }
  };

  const personalTasks = tasks.filter(t => t.task_type === 'personal' || t.assigned_to === user?.id);
  const generalTasks = tasks.filter(t => t.task_type === 'general');
  return <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Community Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Manage community partnerships and business hires
        </p>
      </div>

      <Tabs defaultValue="builder" className="w-full">
        <TabsList>
          <TabsTrigger value="builder">
            <Sparkles className="h-4 w-4 mr-2" />
            AI Letter Builder
          </TabsTrigger>
          <TabsTrigger value="templates">Letter Templates</TabsTrigger>
          <TabsTrigger value="stall-spaces">Stall Spaces</TabsTrigger>
          <TabsTrigger value="sponsorships">Sponsorships</TabsTrigger>
          <TabsTrigger value="donors">Donors</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="space-y-4">
          <LetterBuilder ptaId={ptaId} />
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Donation & Sponsorship Templates</CardTitle>
                  <CardDescription>Pre-written letters for community outreach</CardDescription>
                </div>
                <Button onClick={() => {
                setSelectedTemplate(null);
                setEditTemplateDialog(true);
              }}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {templates.map(template => <div key={template.id} className="p-4 border border-border rounded-lg hover:shadow-card transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-primary" />
                        <div>
                          <h4 className="font-semibold">{template.name}</h4>
                          <Badge variant="outline" className="capitalize">{template.type}</Badge>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => {
                      setSelectedTemplate(template);
                      setEditTemplateDialog(true);
                    }}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>)}
                {templates.length === 0 && <p className="text-center text-muted-foreground py-8">No templates yet. Create your first template.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stall-spaces" className="space-y-4">
          <Card className="shadow-card">
            
            
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Stall Applications</CardTitle>
              <CardDescription>Review and manage business stall applications</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Keep Future</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map(app => <TableRow key={app.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Store className="h-4 w-4 text-primary" />
                          <div>
                            <p>{app.business_name}</p>
                            <p className="text-xs text-muted-foreground">{app.contact_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p>{app.events?.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(app.events?.event_date).toLocaleDateString()}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{app.contact_email}</p>
                          <p className="text-muted-foreground">{app.contact_phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={app.status === "approved" ? "default" : app.status === "declined" ? "destructive" : "secondary"}>
                          {app.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {app.keep_for_future ? "Yes" : "No"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {app.status === "pending" && <>
                              <Button size="icon" variant="ghost" onClick={() => handleApproveApplication(app.id)} title="Approve">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => handleDeclineApplication(app.id)} title="Decline">
                                <XCircle className="h-4 w-4 text-red-600" />
                              </Button>
                            </>}
                          <Button size="icon" variant="ghost" onClick={() => handleDeleteApplication(app.id)} title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
              {applications.length === 0 && <p className="text-center text-muted-foreground py-8">No applications yet</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sponsorships" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Business Sponsorships</CardTitle>
                  <CardDescription>Track business and individual sponsorships</CardDescription>
                </div>
                <Button onClick={() => {
                setSelectedSponsorship(null);
                setEditSponsorshipDialog(true);
              }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Sponsorship
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Purpose</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sponsorships.map(sponsorship => <TableRow key={sponsorship.id}>
                      <TableCell className="font-medium">{sponsorship.name}</TableCell>
                      <TableCell className="font-semibold text-green-600">
                        £{Number(sponsorship.amount).toFixed(2)}
                      </TableCell>
                      <TableCell>{sponsorship.purpose}</TableCell>
                      <TableCell>
                        {new Date(sponsorship.sponsored_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => {
                        setSelectedSponsorship(sponsorship);
                        setEditSponsorshipDialog(true);
                      }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDeleteSponsorship(sponsorship.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
              {sponsorships.length === 0 && <p className="text-center text-muted-foreground py-8">No sponsorships recorded yet</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Tasks</CardTitle>
              <CardDescription>Manage your personal and general tasks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleAddTask} className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="Task title..."
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    className="flex-1"
                    required
                  />
                  <Input
                    placeholder="Description (optional)..."
                    value={newTaskDescription}
                    onChange={(e) => setNewTaskDescription(e.target.value)}
                    className="flex-1"
                  />
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1 space-y-2">
                    <Label>Task Type</Label>
                    <Select value={taskType} onValueChange={(val: 'personal' | 'general') => setTaskType(val)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal">Personal (only you)</SelectItem>
                        <SelectItem value="general">General (visible to all)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Task
                  </Button>
                </div>
              </form>

              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Personal Tasks</h3>
                  <div className="space-y-2">
                    {personalTasks.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No tasks yet</p>
                    ) : (
                      personalTasks.map((task) => (
                        <div key={task.id} className="flex items-center gap-2 p-3 border rounded-lg">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleTask(task.id, task.status)}
                          >
                            {task.status === 'completed' ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            ) : (
                              <Circle className="h-5 w-5" />
                            )}
                          </Button>
                          <div className="flex-1">
                            <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            {task.description && (
                              <p className="text-sm text-muted-foreground">{task.description}</p>
                            )}
                          </div>
                          {task.task_type === 'personal' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTask(task.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-medium mb-2">General Tasks (Visible to All)</h3>
                  <div className="space-y-2">
                    {generalTasks.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No general tasks</p>
                    ) : (
                      generalTasks.map((task) => (
                        <div key={task.id} className="p-3 border rounded-lg bg-muted/30">
                          <p className="font-medium">{task.title}</p>
                          {task.description && (
                            <p className="text-sm text-muted-foreground">{task.description}</p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="donors" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Donor Contacts</CardTitle>
                  <CardDescription>Track businesses and people you've contacted for donations</CardDescription>
                </div>
                <Button onClick={() => {
                  setSelectedDonor(null);
                  setEditDonorDialog(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Donor
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search and Filter Section */}
              <div className="space-y-4 mb-6 p-4 border rounded-lg bg-muted/30">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="donorSearch">Search by Name</Label>
                    <Input
                      id="donorSearch"
                      placeholder="Search donors..."
                      value={donorSearchTerm}
                      onChange={(e) => setDonorSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="donorStatusFilter">Donation Status</Label>
                    <Select value={donorStatusFilter} onValueChange={setDonorStatusFilter}>
                      <SelectTrigger id="donorStatusFilter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="donated">Donated</SelectItem>
                        <SelectItem value="not_donated">No Donation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="donorResponseFilter">Response Status</Label>
                    <Select value={donorResponseFilter} onValueChange={setDonorResponseFilter}>
                      <SelectTrigger id="donorResponseFilter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="responded">Responded</SelectItem>
                        <SelectItem value="not_responded">No Response</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="donorDateFrom">Contact Date From</Label>
                    <Input
                      id="donorDateFrom"
                      type="date"
                      value={donorDateFrom}
                      onChange={(e) => setDonorDateFrom(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="donorDateTo">Contact Date To</Label>
                    <Input
                      id="donorDateTo"
                      type="date"
                      value={donorDateTo}
                      onChange={(e) => setDonorDateTo(e.target.value)}
                    />
                  </div>

                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setDonorSearchTerm("");
                        setDonorStatusFilter("all");
                        setDonorResponseFilter("all");
                        setDonorDateFrom("");
                        setDonorDateTo("");
                      }}
                      className="w-full"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>Showing {filteredDonors.length} of {donors.length} donors</span>
                </div>
              </div>
              {donors.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No donor contacts recorded yet</p>
                  <p className="text-sm">Start tracking your donation outreach</p>
                </div>
              ) : filteredDonors.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No donors match your filters</p>
                  <p className="text-sm">Try adjusting your search criteria</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact Date</TableHead>
                      <TableHead>Contact Method</TableHead>
                      <TableHead>Response</TableHead>
                      <TableHead>Donation Status</TableHead>
                      <TableHead>What They Donated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDonors.map((donor) => (
                      <TableRow key={donor.id}>
                        <TableCell className="font-medium">{donor.name}</TableCell>
                        <TableCell>{new Date(donor.contact_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {donor.contact_methods?.map((method: string) => (
                              <Badge key={method} variant="outline" className="text-xs">
                                {method.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {donor.responded ? (
                            <Badge variant="default" className="bg-blue-600">Responded</Badge>
                          ) : (
                            <Badge variant="secondary">No Response</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {donor.donated ? (
                            <Badge variant="default" className="bg-green-600">Donated</Badge>
                          ) : (
                            <Badge variant="secondary">No Donation</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {donor.donation_details ? (
                            <span className="text-sm">{donor.donation_details}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setSelectedDonor(donor);
                              setEditDonorDialog(true);
                            }}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteDonor(donor.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Stall Application Settings</CardTitle>
              <CardDescription>Configure your stall application form and share the link</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Accept Applications</h4>
                    <p className="text-sm text-muted-foreground">Allow businesses to apply for stall spaces</p>
                  </div>
                  <Switch checked={settings?.accept_applications ?? true} onCheckedChange={(checked) => {
                    handleUpdateSettings("accept_applications", checked);
                  }} />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Require Payment</h4>
                    <p className="text-sm text-muted-foreground">Applicants must confirm payment arrangements</p>
                  </div>
                  <Switch checked={settings?.require_payment ?? true} onCheckedChange={(checked) => {
                    handleUpdateSettings("require_payment", checked);
                  }} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="prohibited-items">Prohibited Items</Label>
                <Textarea id="prohibited-items" placeholder="List items that cannot be sold at stalls..." value={settings?.prohibited_items || ""} onBlur={(e) => {
                  handleUpdateSettings("prohibited_items", e.target.value);
                }} rows={4} />
                <p className="text-xs text-muted-foreground">
                  These items will be displayed on the application form
                </p>
              </div>

              <div className="space-y-2">
                <Label>Application Link</Label>
                <div className="flex gap-2">
                  <Input value={applicationLink} readOnly className="font-mono text-sm" />
                  <Button onClick={copyToClipboard} variant="outline">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Share this link with businesses who want to apply for stall spaces
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EditLetterTemplateDialog open={editTemplateDialog} onOpenChange={setEditTemplateDialog} template={selectedTemplate} ptaId={ptaId} onSuccess={() => {
      fetchTemplates();
      setEditTemplateDialog(false);
    }} />

      <EditSponsorshipDialog open={editSponsorshipDialog} onOpenChange={setEditSponsorshipDialog} sponsorship={selectedSponsorship} ptaId={ptaId} userId={user?.id || ""} onSuccess={() => {
      fetchSponsorships();
      setEditSponsorshipDialog(false);
    }} />

      <EditDonorDialog 
        open={editDonorDialog} 
        onOpenChange={setEditDonorDialog} 
        donor={selectedDonor} 
        ptaId={ptaId} 
        onSuccess={() => {
          fetchDonors();
          setEditDonorDialog(false);
        }} 
      />
    </div>;
}